import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { doc, getDoc } from "firebase/firestore";

import StudentTopBar from "../../components/Student/StudentTopBar";
import AssignmentCardsRow from "../../components/Student/AssignmentCardsRow";
import useStudentAssignments from "../../hooks/useStudentAssignments";
import useAuth from "../../hooks/useAuth";

import { logout } from "../../utils/authUtils";
import { db } from "../../firebase/firebase";

const StudentDashboard = () => {
  const { assignments, loading } = useStudentAssignments();

  // Be resilient: different pages/hooks often expose this differently.
  const auth = useAuth();
  const user = auth?.user ?? null;
  const possibleProfile =
    auth?.userProfile ?? auth?.profile ?? auth?.userData ?? auth?.currentUserData ?? null;

  const [fallbackName, setFallbackName] = useState("");

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  // Primary: any already-loaded profile data
  const nameFromAuth = useMemo(() => {
    const first =
      possibleProfile?.firstName ||
      possibleProfile?.firstname ||
      possibleProfile?.name ||
      user?.displayName ||
      "";

    return typeof first === "string" ? first : "";
  }, [possibleProfile, user]);

  // Fallback: pull from Firestore if auth hook doesn't provide profile
  useEffect(() => {
    let mounted = true;

    const loadName = async () => {
      if (nameFromAuth) return;
      if (!user?.uid) return;

      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        if (!mounted) return;

        const data = snap.exists() ? snap.data() : null;
        const firstName = data?.firstName || "";
        setFallbackName(typeof firstName === "string" ? firstName : "");
      } catch (err) {
        console.error("Failed to load student name:", err);
      }
    };

    loadName();

    return () => {
      mounted = false;
    };
  }, [user?.uid, nameFromAuth]);

  const studentName = nameFromAuth || fallbackName || "Student";

  return (
    <Box sx={{ position: "relative", zIndex: 1}}>
      <StudentTopBar name={studentName} onLogout={handleLogout} />

      <AssignmentCardsRow
        assignments={loading ? [{ id: "loading", loading: true }] : assignments}
      />
    </Box>
  );
};

export default StudentDashboard;
